import { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Reveal } from '../components/Reveal';
import { Star } from '../components/icons/WhatsAppIcon';
import { ContactForm, ContactAside } from '../components/ContactForm';
import { Check, Target, Eye, MessageCircle, LayoutDashboard, Settings, Shield, Zap, Heart, MapPin, Award, Play } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { PAGE_SEO } from '../seo/site';

const TEAM_IMAGE = 'https://customer-assets.emergentagent.com/job_digital-gastro/artifacts/zjyjgxlt_Ohne%20Titel.jpg';

const GRID_IMAGES = {
  onboarding: 'https://static.prod-images.emergentagent.com/jobs/b6ddd0d8-fe2a-42de-aa0e-08ce2ce8ea2f/images/3f7fe066c4d6a0a2207ff4b1e0dfa14d0f82fe837697d942d77ad6a3627e2696.png',
  payment: 'https://static.prod-images.emergentagent.com/jobs/b6ddd0d8-fe2a-42de-aa0e-08ce2ce8ea2f/images/93f912cea3e6b78c23fa710fac7ff4a07d269d6cd3d2ed91fda11ecff18e61de.png',
  service: 'https://static.prod-images.emergentagent.com/jobs/b6ddd0d8-fe2a-42de-aa0e-08ce2ce8ea2f/images/ddea59dd17dec88e9c152a84f88fc4a318470c89e78af5161b28d2dc716b493b.png',
  selfOrder: 'https://static.prod-images.emergentagent.com/jobs/b6ddd0d8-fe2a-42de-aa0e-08ce2ce8ea2f/images/ef9fa2bdaebe889468ff7434216fa555d57a1b56068cd45e4747c49991b875cc.png',
  consultation: 'https://static.prod-images.emergentagent.com/jobs/b6ddd0d8-fe2a-42de-aa0e-08ce2ce8ea2f/images/a49ff15bd73d30795d5deab1b4edbe34c2d1655628d5e25ae9de01e506ea7131.png',
};

const PLATFORM_BENEFITS = [
  { icon: Check, title: 'Transparente Empfehlungen über mehrere Anbieter hinweg', description: 'Wir vergleichen unterschiedliche Lösungen und empfehlen, was wirklich zu deinem Betrieb passt.' },
  { icon: Target, title: 'Klare Prozesse & Orientierung', description: 'Wir übersetzen IT-Sprache in Gastro-Realität und führen dich Schritt für Schritt durch deine Digitalthemen.' },
  { icon: MessageCircle, title: 'Persönlicher Support, der hilft', description: 'Unser Team kennt die Tools aus der Praxis und ist bei Fragen oder Störungen direkt erreichbar.' },
  { icon: LayoutDashboard, title: 'Ein Dashboard für alle Systeme', description: 'Deine digitalen Tools, Verträge und Tickets zentral im Blick – statt zehn Logins und E-Mail-Chaos.' },
  { icon: Settings, title: 'Setup, Verhandlung & Training aus einer Hand', description: 'Wir kümmern uns um Konditionen, Einrichtung und Schulung Ihres Teams.' },
];

const STATS = [
  { value: '98 %', label: 'Weiterempfehlungsrate im Onboarding & Support' },
  { value: '5 min', label: 'Ø Antwortzeit für Premium-Kunden' },
  { value: '500+', label: 'Service-Fälle pro Jahr – schnell gelöst' },
  { value: '60+', label: 'Tools, die wir regelmäßig einrichten & betreuen' },
  { value: '90 % weniger', label: 'Bis zu 90 % geringere Kosten durch die Auswahl passender Systeme' },
  { value: '30 % mehr', label: 'Bis zu 30 % mehr Umsatz pro Tisch dank optimierter Prozesse' },
];

const FOUNDER_VIDEOS = [
  { videoId: '_VseoNtuuZs', title: 'Unsere Produkt- und Serviceauswahl', name: 'Maximilian Schneider-Ludorff', role: 'Gründer' },
  { videoId: '1-T2xWZj-T8', title: 'Technische Experten für die schwierigen Fälle', name: 'Ibo Sy', role: 'Technische Leitung' },
];

const VALUES = [
  { icon: Shield, title: 'Unabhängig & ehrlich', description: 'Wir empfehlen nur Lösungen, die wirklich zu deinem Konzept und deiner Größe passen – ohne verstecktes Kleingedrucktes.' },
  { icon: Zap, title: 'Schnell & zuverlässig', description: 'Unser Premium-Support antwortet in Minuten, nicht in Tagen – und bleibt dran, bis es läuft.' },
  { icon: Heart, title: 'Digital kompetent, aber menschlich', description: 'Technologie ist wichtig – echte Menschen sind entscheidend. Wir sprechen deine Sprache, nicht nur die der Hersteller.' },
  { icon: MapPin, title: 'Nah an der Gastronomie', description: 'Wir arbeiten täglich mit Gastronom:innen zusammen und kennen die Realität zwischen Mittagsservice und Spätschicht.' },
  { icon: Award, title: 'Qualität & Verlässlichkeit', description: 'Wir denken in langfristigen Partnerschaften, nicht in schnellen Abschlüssen.' },
];

const IMAGE_GRID = [
  { image: GRID_IMAGES.onboarding, label: 'Onboarding vor Ort', span: 'col-span-2 row-span-1' },
  { image: GRID_IMAGES.payment, label: 'Kassen- & Payment Setup', span: 'col-span-1 row-span-1' },
  { image: GRID_IMAGES.service, label: 'Digitale Prozesse im Service', span: 'col-span-1 row-span-1' },
  { image: GRID_IMAGES.selfOrder, label: 'Self-Order-Terminals', span: 'col-span-1 row-span-2' },
  { image: GRID_IMAGES.consultation, label: 'Abstimmung mit dem Betrieb', span: 'col-span-1 row-span-2' },
];

const CONTACT_CHECKS = ['Kostenfreie Erstberatung', 'Unverbindliche Systemempfehlungen', 'Persönlicher Ansprechpartner'];

const IconTile = ({ icon: Icon }) => (
  <span className="inline-flex w-12 h-12 items-center justify-center border-2 border-ink bg-purple text-off flex-shrink-0">
    <Icon className="w-6 h-6" />
  </span>
);

const FounderPolaroid = ({ video, index, playing, onPlay }) => (
  <figure className={`polaroid polaroid--r${index + 1}`} data-testid={`founder-video-${index}`}>
    <div className="polaroid__media polaroid__media--portrait">
      {playing ? (
        <iframe
          src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          <img src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`} alt={video.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <button
            type="button"
            onClick={onPlay}
            className="absolute inset-0 flex items-center justify-center bg-ink/30 hover:bg-ink/40 transition-colors group"
            aria-label={`Video abspielen: ${video.title}`}
            data-testid={`play-founder-video-${index}`}
          >
            <span className="polaroid__play group-hover:scale-110">
              <Play className="w-6 h-6 ml-1 text-ink" fill="#17102B" />
            </span>
          </button>
        </>
      )}
    </div>
    <figcaption>
      <span className="polaroid__name block">{video.title}</span>
      <div className="polaroid__cat">{video.name} – {video.role}</div>
    </figcaption>
  </figure>
);

const UeberUnsPage = () => {
  useSeo(PAGE_SEO.ueberUns);
  const [playingVideo, setPlayingVideo] = useState(null);

  return (
    <>
      <Header />
      <main data-testid="ueber-uns-page">

        {/* Hero */}
        <section className="sec bg-off relative overflow-hidden">
          <div className="hero-half hidden lg:block" aria-hidden="true" />
          <Reveal className="wrap relative z-10 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-6 md:mb-8"><Star />Über uns</p>
              <h1 className="display-1 max-w-[16ch] mb-6">
                Der Partner an <span className="serif-it text-purple">deiner Seite</span>
              </h1>
              <p className="font-display font-semibold text-xl md:text-2xl leading-snug mb-6 max-w-[34ch]">
                Mehr Zeit für echte Gastfreundschaft. Weniger Stress mit digitalen Tools.
              </p>
              <p className="text-lg max-w-[60ch]">
                Wir sind die Gastro-Digital-Beratung für Betriebe, die Digitalisierung einfach, passend und gut betreut umsetzen wollen. Unsere Aufgabe: Technik verständlich machen, die richtigen Systeme auswählen und im Alltag verlässlich an deiner Seite sein.
              </p>
            </div>
            <div className="lg:col-span-5">
              <figure className="polaroid polaroid--r2 max-w-[520px] mx-auto lg:ml-auto lg:mr-0">
                <div className="polaroid__media aspect-[4/3]">
                  <img src={TEAM_IMAGE} alt="Gastro-Beratung Team" className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <figcaption>
                  <span className="polaroid__name block">Das Team von gastro-beratung.de</span>
                  <div className="polaroid__cat">Hamburg</div>
                </figcaption>
              </figure>
            </div>
          </Reveal>
        </section>

        {/* Mission & Vision */}
        <section className="sec sec--deep">
          <Reveal className="wrap grid md:grid-cols-2 gap-8 md:gap-10">
            <div className="card-ink card-ink--off p-8 md:p-10">
              <IconTile icon={Target} />
              <p className="label-caps text-purple mt-6 mb-2">Mission</p>
              <h2 className="display-3 mb-4">Unsere Mission</h2>
              <p className="text-lg leading-relaxed">
                Die Digitalisierung in der Gastronomie einfach, verständlich und verlässlich zu machen – für jeden Betrieb, egal ob Café, Bar, Restaurant oder Systemgastronomie.
              </p>
            </div>
            <div className="card-ink card-ink--off p-8 md:p-10">
              <IconTile icon={Eye} />
              <p className="label-caps text-purple mt-6 mb-2">Vision</p>
              <h2 className="display-3 mb-4">Unsere Vision</h2>
              <p className="text-lg leading-relaxed">
                Eine Branche, in der digitale Prozesse reibungslos funktionieren und Gastronom:innen sich auf das konzentrieren können, was wirklich zählt: großartige Erlebnisse für ihre Gäste.
              </p>
            </div>
          </Reveal>
        </section>

        {/* Platform */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <p className="label-caps text-purple mb-4">Plattform</p>
            <h2 className="display-2 max-w-[20ch] mb-5">
              Unsere Plattform: <span className="serif-it text-purple">Gastro-Beratung.de</span>
            </h2>
            <p className="font-display font-semibold text-xl md:text-2xl leading-snug mb-5 max-w-[36ch]">
              Eine Plattform für deine komplette digitale Gastro-Landschaft.
            </p>
            <p className="text-lg max-w-[70ch] mb-12 md:mb-16">
              Auf der ersten digitalen Gastro-Plattform im Markt, die persönliche Beratung mit einer smarten Lösung verbindet bündeln wir die wichtigsten digitalen Systeme entlang deiner Wertschöpfungskette – von Kasse über Reservierung und Warenwirtschaft bis Personalplanung. Mit klaren Empfehlungen, nahtloser Einrichtung und schnellem, persönlichem Support.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {PLATFORM_BENEFITS.map((benefit) => (
                <div key={benefit.title} className="card-ink card-ink--hover p-6">
                  <IconTile icon={benefit.icon} />
                  <h3 className="font-display font-bold text-xl mt-5 mb-2 leading-snug">{benefit.title}</h3>
                  <p className="text-[0.98rem]">{benefit.description}</p>
                </div>
              ))}
              <div className="card-ink card-ink--lilac p-6 flex flex-col justify-center">
                <span className="text-purple text-3xl mb-3" aria-hidden="true">✳</span>
                <p className="text-[1rem] leading-relaxed">
                  <strong className="font-display">Unsere Beratung ist kostenfrei.</strong> Für viele Softwarelösungen erhalten wir Provisionen von unseren Partnern. Deine Konditionen werden dadurch nicht schlechter – oft können wir sogar bessere Pakete für dich verhandeln.
                </p>
              </div>
            </div>
          </Reveal>
        </section>

        {/* Stats */}
        <section className="sec sec--purple">
          <Reveal className="wrap">
            <h2 className="display-2 max-w-[20ch] mb-5">Was unsere Arbeit <span className="serif-it text-lilac">messbar</span> macht.</h2>
            <p className="text-lg opacity-90 max-w-[60ch] mb-12 md:mb-16">
              Wir begleiten Gastronomien nicht nur bei der Auswahl digitaler Tools – wir sorgen dafür, dass sie echten Mehrwert im Alltag bringen.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-10">
              {STATS.map((stat) => (
                <div key={stat.label} className="border-t-2 border-off pt-5">
                  <div className="font-display font-extrabold text-3xl md:text-5xl leading-none mb-3 text-lilac">{stat.value}</div>
                  <p className="text-[0.98rem] opacity-90">{stat.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        {/* Founder videos */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <h2 className="display-2 max-w-[20ch] mb-4">Einblicke in unser Team</h2>
            <p className="text-lg max-w-[56ch] mb-12 md:mb-16">Unsere Gründer erzählen, wie sie Probleme in der Gastronomie lösen.</p>
            <div className="grid md:grid-cols-2 gap-10 md:gap-16 max-w-[360px] md:max-w-[760px]">
              {FOUNDER_VIDEOS.map((video, index) => (
                <FounderPolaroid
                  key={video.videoId}
                  video={video}
                  index={index}
                  playing={playingVideo === video.videoId}
                  onPlay={() => setPlayingVideo(video.videoId)}
                />
              ))}
            </div>
          </Reveal>
        </section>

        {/* Image grid */}
        <section className="sec sec--lilac">
          <Reveal className="wrap">
            <p className="label-caps text-purple mb-4">Einblicke</p>
            <h2 className="display-2 max-w-[20ch] mb-10 md:mb-14">Aus unserem Arbeitsalltag</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 auto-rows-[160px] md:auto-rows-[220px]">
              {IMAGE_GRID.map((item) => (
                <figure key={item.label} className={`relative border-2 border-ink overflow-hidden bg-deep ${item.span}`}>
                  <img src={item.image} alt={item.label} loading="lazy" className="w-full h-full object-cover" />
                  <figcaption className="absolute bottom-3 left-3 badge-ink badge-ink--white normal-case tracking-normal text-[0.85rem] font-display font-bold">
                    {item.label}
                  </figcaption>
                </figure>
              ))}
              <div className="col-span-1 row-span-1 border-2 border-ink bg-purple text-off flex items-center justify-center p-6 text-center">
                <p className="font-display font-bold leading-snug">Dein Partner für digitale Gastronomie</p>
              </div>
            </div>
          </Reveal>
        </section>

        {/* Values */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <h2 className="display-2 mb-12 md:mb-16">Wofür wir <span className="serif-it text-purple">stehen.</span></h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {VALUES.map((value) => (
                <div key={value.title} className="card-ink card-ink--hover p-6">
                  <IconTile icon={value.icon} />
                  <h3 className="font-display font-bold text-xl mt-5 mb-2">{value.title}</h3>
                  <p className="text-[0.98rem]">{value.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        {/* History */}
        <section className="sec sec--deep">
          <Reveal className="wrap grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <h2 className="display-2">Wie alles begann – und <span className="serif-it text-lilac">wohin</span> wir gehen.</h2>
            </div>
            <div className="lg:col-span-7 text-lg leading-relaxed space-y-5 opacity-95">
              <p>
                gastro-beratung.de wurde gegründet, weil Digitalisierung in vielen Betrieben unnötig kompliziert war: zu viele Tools, zu wenig Orientierung, zu wenig Support. Statt sich auf Gäste zu konzentrieren, mussten sich Gastronom:innen durch Vergleichsportale, Hersteller-Hotlines und halbfertige Lösungen kämpfen.
              </p>
              <p>
                Heute begleiten wir Betriebe in ganz Deutschland – vom Café über Bars und Restaurants bis zur Systemgastronomie – und entwickeln die Gastro-Digitalplattform, die alle relevanten Systeme bündelt und echten Support in den Mittelpunkt stellt.
              </p>
            </div>
          </Reveal>
        </section>

        {/* Contact */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <h2 className="display-2 mb-10 md:mb-16">Lernen wir uns kennen.</h2>
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-5">
                <ul className="rule-list mb-10">
                  {CONTACT_CHECKS.map((c) => (
                    <li key={c} className="flex items-center gap-3 font-display font-bold text-lg"><Star />{c}</li>
                  ))}
                </ul>
                <ContactAside
                  intro="Ob du am Anfang deiner digitalen Reise stehst oder bestehende Systeme sortieren möchtest – schreib uns kurz, wir melden uns persönlich."
                  title="Oder direkt per WhatsApp Kontakt aufnehmen"
                  testIdPrefix="about-"
                />
              </div>
              <div className="lg:col-span-7" data-testid="contact-form-about">
                <ContactForm testIdPrefix="about-" idSuffix="-about" />
              </div>
            </div>
          </Reveal>
        </section>

      </main>
      <Footer />
    </>
  );
};

export default UeberUnsPage;
